import type { CommandHandler } from '../types';
import { extractFieldKeys } from '../../utils';
import dayjs from 'dayjs';
import { store } from '../../../../app/store';
import { gridApi } from '../../api/gridApi';

/**
 * Per-grid cycle state for changeTotal command.
 * Key: metadata.contentKey, Value: ChangeTotalState
 *
 *   All     → no filter, no network call
 *   Client  → in-memory filter, no network call
 *   Project → network call, then resets so next click is All again
 */
const ChangeTotalState = {
  All: 0,
  Client: 1,
  Project: 2,
} as const;

type ChangeTotalState = (typeof ChangeTotalState)[keyof typeof ChangeTotalState];

const TITLES: Record<ChangeTotalState, string> = {
  [ChangeTotalState.All]: 'Timesheet (All)',
  [ChangeTotalState.Client]: 'Timesheet (by Client)',
  [ChangeTotalState.Project]: 'Timesheet (by Project)',
};

const cycleState = new Map<string, ChangeTotalState>();

function getNextState(key: string): ChangeTotalState {
  const current = cycleState.get(key) ?? ChangeTotalState.All;
  const next =
    current + 1 > ChangeTotalState.Project
      ? ChangeTotalState.All
      : ((current + 1) as ChangeTotalState);
  cycleState.set(key, next);
  return next;
}

export function resetChangeTotalState(contentKey: string): void {
  cycleState.delete(contentKey);
}

export function getChangeTotalState(contentKey: string): number {
  return cycleState.get(contentKey) ?? ChangeTotalState.All;
}

function isNonEmptyTimesheetRow(row: Record<string, unknown>): boolean {
  const projectIdIsNotZero = row['ProjectId'] == null || Number(row['ProjectId']) !== 0;
  const isEmpty =
    row['Description'] === '' &&
    row['LogStart'] == null &&
    row['Assigned'] === '' &&
    projectIdIsNotZero;
  return !isEmpty;
}

export const changeTotalHandler: CommandHandler = async (_command, context) => {
  const { metadata, gridData, dateRange, onReplaceGridView } = context;

  if (!onReplaceGridView) {
    console.warn('[changeTotal] onReplaceGridView callback is not provided');
    return { success: false, error: 'View replacement callback not available' };
  }

  const contentKey = metadata.contentKey;
  const state = getNextState(contentKey);

  if (state === ChangeTotalState.Client) {
    const filtered = gridData.filter(isNonEmptyTimesheetRow);
    onReplaceGridView(filtered, TITLES[ChangeTotalState.Client]);
    return { success: true };
  }

  const startDate = dateRange?.startDate;
  const endDate = dateRange?.endDate;

  try {
    const cols = Array.isArray(metadata.columns)
      ? (metadata.columns as import('../../types').ColumnMetadata[])
      : undefined;
    const fieldKeys = extractFieldKeys(cols);

    const result = await store.dispatch(
      gridApi.endpoints.getGridData.initiate(
        {
          options: {
            nodeKey: contentKey,
            fieldKeys,
            parentId: '',
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
            ...(startDate && {
              rowKeys: `${dayjs(startDate).format('MM/DD/YYYY')}&endDate`,
            }),
          },
          columnMetadata: cols,
        },
        { forceRefetch: true, subscribe: false }
      )
    );

    if (result.error) {
      return { success: false, error: 'Failed to load timesheet data' };
    }

    let rows = [...(result.data?.data ?? [])];

    if (state === ChangeTotalState.Project) {
      rows = rows.filter((row) => Number(row['ProjectId']) !== 0);
    }

    onReplaceGridView(rows, TITLES[state]);
    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to load timesheet data',
    };
  }
};
